import { LeaderboardRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import Chart from 'react-apexcharts';
import MainCard from '../../../common/cards/MainCard';
import { weeklyTransactionChartProps } from '../../variables';
import ChartSkeleton from '../skeletons/ChartSkeleton';

const chartChoices = [
  {
    value: 'netamount',
    label: 'Net Amount',
  },
  {
    value: 'count',
    label: 'Count',
  },
];

export default function WeeklyTransactionChart({ isLoading, data }) {
  const theme = useTheme();
  const [chartType, setChartType] = useState('netamount');

  if (isLoading) return <ChartSkeleton height={480} />;

  const { primary } = theme.palette.text;
  const grey200 = theme.palette.grey[200];
  const grey500 = theme.palette.grey[500];

  const primary200 = theme.palette.primary[200];
  const primaryDark = theme.palette.primary.dark;
  const secondaryMain = theme.palette.secondary.main;
  const secondaryLight = theme.palette.secondary.light;
  const extendedStyles = {
    colors: [secondaryMain, primaryDark, primary200, secondaryLight],
    xaxis: {
      labels: {
        style: {
          colors: [
            primary,
            primary,
            primary,
            primary,
            primary,
            primary,
            primary,
          ],
        },
      },
      ...weeklyTransactionChartProps.options.xaxis,
    },
    yaxis: {
      labels: {
        style: {
          colors: [primary],
        },
      },
    },
    grid: {
      borderColor: grey200,
    },
    tooltip: {
      theme: 'light',
      enabled: true,
    },
    legend: {
      labels: {
        colors: grey500,
      },
    },
    fill: {
      opacity: 1,
      type: chartType === 'count' ? 'solid' : 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.5,
        stops: [],
      },
      colors: [secondaryMain, primaryDark, primary200, secondaryLight],
    },
    stroke: {
      curve: 'smooth',
    },
    dataLabels: {
      enabled: false,
    },
  };

  // Handle data
  const emptySeries = [
    { name: 'No Transactions', data: [0, 0, 0, 0, 0, 0, 0] },
  ];

  // Raw data for transactions.
  const transactions = data.listTransaction || [];

  // ###### Datetime handling ######
  // This array will have 7 elements, each one contains data of one day.
  const weeklyTransactions = [];

  // Grouping transactions by date.
  const groupedTransactions = transactions.reduce((items, element) => {
    const date = element.transdate.split('T')[0];
    if (!items[date]) {
      items[date] = [];
    }
    items[date].push(element);
    return items;
  }, {});

  // Matching transaction data to 7 last days.
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);

    // Convert date to YYYY-MM-DD format.
    const formatDate = d.toISOString().split('T')[0];

    // Push to array, matching it's date.
    weeklyTransactions.push({
      date: formatDate,
      transactions: groupedTransactions[formatDate] || [],
    });
  }

  // ###### Transaction types handling ######
  // Auto-detect value of key and grouping object's data by this key.
  const groupBy = (items, key) =>
    items.reduce(
      (result, item) => ({
        ...result,
        [item[key]]: [...(result[item[key]] || []), item],
      }),
      {}
    );

  // Detect and group transactions by "transtypeid" key.
  // Then, get the transTypes by all difference "transtypeid" detected.
  const transTypes = Object.keys(groupBy(transactions, 'transtypeid'));

  // ###### Series handling ######
  // Temporary Series, to be use as data series for the chart
  const tmpSeriesTransCount = [];

  transTypes.forEach((type) => {
    // Series for one "transtypeid".
    tmpSeriesTransCount.push({
      name: type,
      data: weeklyTransactions
        .map(
          (ele) => ele.transactions.filter((e) => e.transtypeid === type).length
        )
        .reverse(),
    });
  });

  const tmpSeriesTransNet = [
    {
      name: 'Transactions Net Amount',
      data: weeklyTransactions
        .map((daily) =>
          daily.transactions.reduce(
            (dailyNet, transaction) =>
              dailyNet + parseInt(transaction.netamount),
            0
          )
        )
        .reverse(),
    },
  ];

  const renderWeeklyTransactionStatsChartProps = {
    ...weeklyTransactionChartProps,
  };

  renderWeeklyTransactionStatsChartProps.series =
    transactions.length === 0
      ? emptySeries
      : chartType === 'count'
      ? tmpSeriesTransCount
      : tmpSeriesTransNet;

  renderWeeklyTransactionStatsChartProps.options = {
    ...renderWeeklyTransactionStatsChartProps.options,
    ...extendedStyles,
  };

  return (
    <MainCard>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <Box sx={{ textAlign: 'left' }} height="40px">
                    <Button
                      endIcon={<LeaderboardRounded />}
                      sx={{ textAlign: 'left', ml: 1, cursor: 'default' }}
                      size="medium"
                    >
                      <Typography variant="h4">Weekly Transactions</Typography>
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <TextField
                select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
              >
                {chartChoices.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Chart
            {...renderWeeklyTransactionStatsChartProps}
            type={chartType === 'count' ? 'bar' : 'area'}
          />
        </Grid>
      </Grid>
    </MainCard>
  );
}
